import React from 'react';
import { filterBy } from 'new/utils/filterBy';
import { connect } from 'react-redux';
import { Button, Stack } from '@mui/material';
import { NEW_CREDS_VALUE } from '../AuthenticateDataSource';
import { setFormError } from 'js/actions';
import CredentialFormContainer from '../../../credentials/CredentialFormContainer';
import { FORM_TYPE_EDIT_CREDENTIAL } from 'js/constants/formTypes';
import FormErrorComponent from '../../../elements/forms/FormErrorComponent';
import { isReadOnly } from 'js/lib/ACLUtils';
import { ModalDefault } from 'new/routes/ModalDefault';
import { ModalAddCredential } from 'new/elements/flows/ModalAddCredential';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { AddCredentialCard } from 'new/elements/flows/EnterCredential/components/AddCredentialCard';
import { SearchBar } from 'new/elements/flows/EnterCredential/components/SearchBar';
import { CredentialsHeader } from 'new/elements/flows/EnterCredential/components/CredentialsHeader';
import { CredentialCard } from 'new/elements/flows/EnterCredential/components/CredentialCard';
import { GET_FULL_VENDOR_REQUEST, GET_VENDOR_AUTH_PARAMS_REQUEST } from 'js/constants/actionTypes';

import './EnterSourceCredentials.scss';

class EnterSourceCredentials extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchText: '',
      currentEditCred: {},
    };
    this.searchBarRef = React.createRef();

    this._handleCredentialInfoUpdated = this._handleCredentialInfoUpdated.bind(this);
    this._handleSearchChanged = this._handleSearchChanged.bind(this);
  }

  isCredentialSelectionEmpty = () => {
    const { selectedCredentialId } = this.props;
    return selectedCredentialId === undefined;
  };

  render() {
    const { isLoading = false } = this.props;
    const ref = this.searchBarRef;
    const onSearchChange = this._handleSearchChanged;

    const searchText = this.state.searchText.toLowerCase();
    const { credsToDisplay = [] } = this.props;
    const regularCreds = credsToDisplay.filter((cred) => cred.value !== NEW_CREDS_VALUE);
    const filteredCreds = !searchText
      ? regularCreds
      : regularCreds.filter(filterBy(searchText, 'name', 'description'));

    const isSelectionEmpty = this.isCredentialSelectionEmpty(); // on-mount cycle: represent if no one credential have been selected yet

    return (
      <Stack flex={1} height="100%" className="enter-credentials">
        <SearchBar ref={ref} onSearchChange={onSearchChange} />

        <div className="credentials-layout">
          <CredentialsHeader
            onRefreshClicked={this.props.onRefreshClicked}
            isLoadingData={this.props.isLoadingData}
          />

          <div className="credential-cards-container">
            <AddCredentialCard onClick={this._handleCredentialSelected(NEW_CREDS_VALUE)} />

            {filteredCreds.map((item) => {
              const uniqueKey = `${item?.id}_${item?.name}`;
              const readOnly = isReadOnly(item);
              return (
                <CredentialCard
                  key={uniqueKey}
                  credential={item}
                  isSelectionEmpty={isSelectionEmpty}
                  selected={
                    !!this.props.selectedCredentialId &&
                    item.value === this.props.selectedCredentialId
                  }
                  onCredentialSelected={(cred, skipAutoRedirection) =>
                    this._handleCredentialSelected(cred.value, skipAutoRedirection)()
                  }
                  onClickEdit={
                    !readOnly
                      ? () => {
                          this._handleCredentialSelected(item.id, true);
                          this.setState({ currentEditCred: { id: item.id } });
                        }
                      : undefined
                  }
                />
              );
            })}
          </div>
        </div>

        {this.state.currentEditCred.id ? (
          <ModalDefault
            maxWidth="md"
            title="Edit Credentials"
            removeContentPadding
            contentClassName="modal-credential-content"
            onCancel={this.onCancelEditCredential}
            config={{ disableClickOut: true }}
          >
            <CredentialFormContainer
              isPopUpForm
              credentialId={this.state.currentEditCred.id}
              renderFooter={this.renderFooter}
              onCancel={this.onCancelEditCredential}
              onError={(error) => {
                this.props.setFormError(FORM_TYPE_EDIT_CREDENTIAL, error);
              }}
              onSubmit={() => {
                // onSubmit fires only after save + probe both succeed
                // (updateCredentialAndProbe resolves on probe success; probe
                // failures hit .catch → onError). Close modal and advance.
                this.setState({ currentEditCred: {} });
                this.props.onEditCredentialSuccess?.();
              }}
            />
          </ModalDefault>
        ) : null}

        {this.props.isNewCredential === true ? (
          <ModalAddCredential
            connectionType={this.props.selectedConnection}
            onCredentialInfoUpdated={this._handleCredentialInfoUpdated}
            onSaveCredential={this.props.onSaveNewCredential}
            selectedCredentialInfo={this.props.selectedCredentialInfo}
            onCloseClicked={this.props.onCloseNewCredentialsClicked}
            onChangeTemplate={this.props.onChangeTemplate}
            connectionMetas={this.props.connectionMetas}
            vendors={this.props.vendors}
            connectionCategory={
              this.props.connectionMetas[this.props.selectedConnection].connectionCategory
            }
            onChangeApiType={this.props.onChangeApiType}
            selectedTemplate={this.props.selectedTemplate}
            connectionAuthMeta={this.props.connectionAuthMeta}
            credentialInputDisabled={this.props.credentialInputDisabled}
            isFormAwaitingValidation={this.props.isFormAwaitingValidation}
            onCredInfoUpdated={this._handleCredentialInfoUpdated}
            onSaveNewCredential={this.props.onSaveNewCredential}
          />
        ) : null}

        <FormErrorComponent
          type={FORM_TYPE_EDIT_CREDENTIAL}
          error={this.props.error}
          dialogIsOpen={this.props.dialogIsOpen}
        />
        {isLoading ? (
          <Stack height="100%" width="100%">
            <CenteredLoader absolute />
          </Stack>
        ) : null}
      </Stack>
    );
  }

  onCancelEditCredential = () => {
    this.setState({ currentEditCred: {} });
  };

  renderFooter = (onSubmit, onCancel) => {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        padding="16px"
        borderTop="1px solid var(--color-greyscale-100)"
      >
        <Button variant="outlined" color="info" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="ghost" color="primary" onClick={onSubmit}>
          Save
        </Button>
      </Stack>
    );
  };

  _handleCredentialSelected = (newValue, skipAutoRedirection) => () => {
    if (newValue === this.props.selectedCredentialId) {
      this.props.onCredSelected(undefined, undefined, skipAutoRedirection);
      return;
    }

    this.props.onCredSelected(newValue, undefined, skipAutoRedirection);
  };

  _handleCredentialInfoUpdated(newCredInfo, field, value) {
    this.props.onCredInfoUpdated(this.props.selectedCredentialId, newCredInfo, field, value);
  }

  _handleSearchChanged(event) {
    this.setState({ searchText: event.target.value });
  }
}

const mapStateToProps = (state) => {
  const formState = state.forms[FORM_TYPE_EDIT_CREDENTIAL];
  const isLoading =
    state.request[GET_FULL_VENDOR_REQUEST] || state.request[GET_VENDOR_AUTH_PARAMS_REQUEST];

  return {
    isLoading,
    error: formState && formState.error,
    dialogIsOpen: formState && formState.dialogIsOpen,
  };
};

export default connect(mapStateToProps, {
  setFormError,
})(EnterSourceCredentials);
